import { pxToRem } from "../../../utils/styles/size";
import { SpaceCircle } from "./SpaceCircle";
import { SpaceType } from "../../../types/space";
import { useJuneTrackCall } from "../../../utils/june/analytics";
import React, { useEffect, useRef, useState } from "react";
import { Flex, Tooltip, Text, Input, Box } from "@chakra-ui/react";
import { createPortal } from "react-dom";
import FilterProjectReorderDragLayer from "../FilterProjectReorderDragLayer";
import { useDragAndDrop } from "../../../hooks/useDragAndDrop";
import { HandleSpaceReorderType } from "./SpacesSection";
import clsx from "clsx";
import { ContextMenu } from "radix-ui";
import { ContextMenuContent } from "../../atom/ContextMenuContent";
import { JUNE_LOCATION } from "../../../hooks/june/juneEvent";

interface SpaceSideBarItemProps {
  data: SpaceType;
  index: number;
  isSelected: boolean;
  onArchive: () => void;
  onDelete: () => void;
  onUpdate: (payload: Partial<SpaceType>) => void;
  onClickSpaceCircle: (e: React.MouseEvent) => void;
  onReorder: HandleSpaceReorderType;
}

const SpaceSideBarItem: React.FC<SpaceSideBarItemProps> = ({
  data,
  index,
  isSelected,
  onArchive,
  onDelete,
  onUpdate,
  onClickSpaceCircle,
  onReorder,
}) => {
  const { color, title } = data;
  const trackCall = useJuneTrackCall();
  const [menuVisible, setMenuVisible] = useState(false);
  const [inputValue, setInputValue] = useState(title);
  const [isEditing, setIsEditing] = useState(false);
  const [prevIndex, setPrevIndex] = useState(index);
  const [showAnimation, setShowAnimation] = useState(true);

  // 최초 마운트 시에만 애니메이션 동작하도록 추가
  useEffect(() => {
    setShowAnimation(false);
  }, []);

  const inputRef = useRef<HTMLInputElement>(null);

  const { ref, isDragging, shouldShowPreview } = useDragAndDrop({
    index,
    data,
    onReorder,
    prevIndex,
    setPrevIndex,
  });

  const handleRename = () => {
    setIsEditing(true);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newInput = e.target.value;
    setInputValue(newInput);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (inputValue.trim() === "") return;

    if (event.key === "Enter") {
      handleSubmitRename();
    }

    if (event.key === "Escape") {
      setIsEditing(false);
    }
  };

  const handleSubmitRename = () => {
    if (inputValue.trim() === "") return;
    onUpdate({ title: inputValue });
    trackCall("update_space", {
      location: JUNE_LOCATION.SIDEBAR,
      type: "title",
    });
    setIsEditing(false);
  };

  const handleChangeColor = (newColor: string) => {
    onUpdate({ color: newColor });
    trackCall("update_space", {
      location: JUNE_LOCATION.SIDEBAR,
      type: "color",
    });
  };

  return (
    <Box
      borderRadius={shouldShowPreview ? "6px" : "initial"}
      bg={shouldShowPreview ? "rgba(255, 255, 255, 0.04)" : "initial"}
      w={"full"}
    >
      <ContextMenu.Root onOpenChange={setMenuVisible}>
        <ContextMenu.Trigger asChild>
          <Flex
            role="group"
            ref={ref}
            justifyContent={"flex-start"}
            alignItems={"center"}
            opacity={shouldShowPreview ? 0 : 1}
            cursor={"pointer"}
            py={pxToRem(4)}
            px={pxToRem(2)}
            className={clsx(
              "rounded-[5px] border border-solid border-transparent",
              "@[220px]/sidebar:hover:bg-[rgba(255,255,255,0.08)]",
              menuVisible &&
                "@[220px]/sidebar:border-[rgba(255,255,255,0.08)] @[220px]/sidebar:bg-[rgba(255,255,255,0.08)]",
              isEditing ? "bg-[rgba(0,0,0,0.4)]" : "bg-transparent",
              showAnimation && "animate-moba-fade-in"
            )}
            onClick={onClickSpaceCircle}
          >
            <div
              className={clsx(
                "border-box flex w-full rounded-[50%]",
                "hover:bg-[rgba(255,255,255,0.08)] @[220px]/sidebar:hover:bg-transparent",
                menuVisible &&
                  "border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.08)] @[220px]/sidebar:border-transparent @[220px]/sidebar:bg-transparent"
              )}
            >
              {/* 사이드바가 열러있을 떄(220px 이상일 때)만 보임 */}
              <div className="hidden @[220px]/sidebar:flex @[220px]/sidebar:items-center">
                <SpaceCircle
                  spaceColor={color}
                  isSelected={isSelected}
                />
                {isEditing ? (
                  <Input
                    ref={inputRef}
                    border={"none"}
                    px={0}
                    h={"fit-content"}
                    bg={"transparent"}
                    _focus={{}}
                    _focusVisible={{}}
                    _active={{}}
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onBlur={handleSubmitRename}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                    ml={2}
                    fontSize={"0.875rem"}
                    color={"#ffffff"}
                  />
                ) : (
                  <Text
                    textStyle={"body1"}
                    color={"#ffffff"}
                    noOfLines={1}
                    ml={2}
                  >
                    {title}
                  </Text>
                )}
              </div>

              {/* 사이드바가 닫혀있을 때(220px 미만일 때)만 보임 */}
              <div className="block @[220px]/sidebar:hidden">
                <Tooltip
                  label={title}
                  placement={"right"}
                  isDisabled={isDragging}
                  closeOnScroll={true}
                >
                  <SpaceCircle
                    spaceColor={color}
                    isSelected={isSelected}
                  />
                </Tooltip>
              </div>
            </div>
          </Flex>
        </ContextMenu.Trigger>
        <ContextMenu.Portal>
          <ContextMenuContent>
            <ContextMenuContent.ColorPicker
              spaceColor={color}
              onChangeSpaceColor={handleChangeColor}
            />
            <ContextMenuContent.Item onClick={handleRename}>Rename</ContextMenuContent.Item>
            <ContextMenuContent.Item onClick={onArchive}>Archive</ContextMenuContent.Item>
            <ContextMenuContent.Item onClick={onDelete}>Delete</ContextMenuContent.Item>
          </ContextMenuContent>
        </ContextMenu.Portal>
      </ContextMenu.Root>
      {shouldShowPreview &&
        createPortal(
          <div style={{ cursor: "pointer" }}>
            <FilterProjectReorderDragLayer />
          </div>,
          document.body
        )}
    </Box>
  );
};

export default SpaceSideBarItem;
